import "./style.css";
import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useHistory } from "react-router-dom";
import UserLogo from 'components/UserLogo';
import useCurrentUser from 'hooks/useCurrentUser';
import C from "Constants";
import SearchInput from './SearchInput';
import { SearchIcon, BasketBag as CartIcon, BackArrow } from "config/Svgs";
import { useSelector } from "react-redux";
import { useLang } from "hooks";
import LanguageToggler from "features/LanguageToggler";

const defaultFn = () => { };

const OrderTypes = [
    {
        name: C.SERVICE_TYPE.DINE_IN,
        type: C.SERVICE_TYPE.DINE_IN
    },
    {
        name: C.SERVICE_TYPE.DELIVERY,
        type: C.SERVICE_TYPE.DELIVERY
    },
    {
        name: C.SERVICE_TYPE.PICKUP,
        type: C.SERVICE_TYPE.PICKUP
    }
];

export const navConfigKeys = {
    search: "search",
    cart: "cart"
}

export const LoginOrProfile = ({ userClick = defaultFn, authClick = defaultFn, userClassName = " ", authClassName = "" }) => {
    const { currentUser } = useCurrentUser();
    return (
        currentUser?.token ? (
            <UserLogo goToSettings={userClick} className={userClassName} />
        ) : (
            <button onClick={authClick} className={`drop-shadow-md order-type-button order-type-button-ar font-semibold hover-button ${authClassName}`}>
                {t("Signup/Login")}
            </button>
        )
    )
}

const UserNavPanel = ({
    orderTypes,
    activeOrderType = null,
    navIconConfig = [],
    searchTerm = "",
    showBackButton = false,
    showLanguageToggle = true,
    containerProps = {},
    searchInputProps = {},
    handleSearch = defaultFn,
    onOrderTypeChange = defaultFn,
    authClick = defaultFn,
    userClick = defaultFn,
    cartClick = defaultFn,
    backButtonClick = defaultFn,
}) => {
    const history = useHistory();
    const { isArabic } = useLang();
    const [isSearchOpen, setIsSearchOpen] = React.useState(false);

    const { cart } = useSelector((state) => state?.cart);

    const NavIconConfig = [
        {
            id: navConfigKeys.search,
            Icon: SearchIcon,
            onClick: () => setIsSearchOpen(true),
        },
        {
            id: navConfigKeys.cart,
            Icon: () => {
                return <div className="relative">
                    <CartIcon />
                    {cart?.length > 0 && <span className="absolute mx-1 top-0 h-4 w-4 rounded-full bg-brand-bgtext text-brand-base-primary text-12x">
                        {cart?.length ?? 0}
                    </span>}
                </div>
            },
            onClick: cartClick
        },
    ];

    return (
        <div 
        {...containerProps}
        className={`location-bar flex justify-between items-center gap-2 flex-wrap relative ${containerProps?.className ?? ""}`} >
            {
                isSearchOpen ?
                    <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", ...searchInputProps?.style }}>
                        <SearchInput onCloseSearch={() => setIsSearchOpen(false)} searchTerm={searchTerm} onSearch={handleSearch} />
                    </div>
                    :
                    <>
                        <div className="flex gap-2 flex-wrap items-center">
                            {
                                showBackButton &&
                                <button onClick={backButtonClick ? backButtonClick : () => history.goBack()} className={"drop-shadow-md svg-stroke h-9 flex items-center border border-brand-base-primary justify-center w-9 rounded-full bg-white hover:bg-brand-base-primary cursor-pointer "}>
                                    <svg
                                        width="8" height="16"
                                        viewBox="0 0 8 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className={`${isArabic ? "-rotate-180" : ""}`}
                                    >
                                        <path
                                            d="M7.25 14.5L0.75 8L7.25 1.5"
                                            stroke="#111827"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            }
                            {
                                showLanguageToggle && 
                                <div className="drop-shadow-md">
                                <LanguageToggler />
                                </div>
                            }

                            {
                                orderTypes && OrderTypes?.map((order, index) => {
                                    const isSelected = orderTypes?.includes(order?.type) ?? false;
                                    return (
                                        <React.Fragment key={index}>
                                            {isSelected ? (
                                                <button
                                                    onClick={onOrderTypeChange}
                                                    className={`drop-shadow-md order-type-button order-type-button-ar font-semibold capitalize ${activeOrderType === order?.type ? "border-brand-base-primary" : ""} p-2`}
                                                >
                                                    {t(order?.name)}
                                                </button>
                                            ) : null}
                                        </React.Fragment>
                                    );
                                })
                            }
                        </div>
                        <div className='flex gap-2 flex-wrap'>
                            {NavIconConfig?.map((icon, index) => {
                                const isAllowed = navIconConfig?.includes(icon?.id) ?? false;
                                const fn = icon?.onClick ?? defaultFn;
                                return (
                                    <React.Fragment key={index}>
                                        {isAllowed ? (
                                            <button onClick={fn} className={"drop-shadow-md svg-stroke h-10 w-10 flex items-center border border-brand-base-primary justify-center rounded-full bg-white hover:bg-brand-base-primary cursor-pointer "}>
                                                <icon.Icon />
                                            </button>
                                        ) : null}
                                    </React.Fragment>
                                )
                            })}
                            <LoginOrProfile userClick={userClick} authClick={authClick} />
                        </div>
                    </>
            }
        </div>
    );
};

// Extract enum values for validation
const validServiceTypes = Object.values(C.SERVICE_TYPE);

// Define and export PropTypes
export const UserNavPanelPropTypes = {
    orderTypes: PropTypes.arrayOf((propValue, key, componentName, location, propFullName) => {
        if (!validServiceTypes.includes(propValue[key])) {
            return new Error(
                `Invalid prop \`${propFullName}[${key}]\` supplied to \`${componentName}\`. ` +
                `Expected one of: ${validServiceTypes.join(', ')}.`
            );
        }
        return null;
    }),
    activeOrderType: PropTypes.oneOf(validServiceTypes),
    navIconConfig: PropTypes.arrayOf(
        PropTypes.oneOf(Object.values(navConfigKeys))
    ),
    searchTerm: PropTypes.string,
    handleSearch: PropTypes.func,
    onOrderTypeChange: PropTypes.func,
    authClick: PropTypes.func,
    userClick: PropTypes.func,
    cartClick: PropTypes.func,
    backButtonClick: PropTypes.func,
    showLanguageToggle: PropTypes.bool,
    showBackButton: PropTypes.bool,
};

// Assign to component
UserNavPanel.propTypes = UserNavPanelPropTypes;

export default UserNavPanel;
